import { useEffect, useMemo, useState } from "react";
import { PhoneCall } from "lucide-react";
import { api } from "@/lib/api";
import { EMERGENCY } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";

// Fallback to the bundled verified list if the API is unreachable — this page must always show numbers.
const fallbackContacts = () =>
  Object.entries(EMERGENCY).flatMap(([country, c]) =>
    c.services.map(([label, number]) => ({ country: c.label, label, number, note: c.note }))
  );

export default function EmergencyPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/emergency/contacts").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const contacts = data?.contacts?.length ? data.contacts : fallbackContacts();
  const reviewed = data?.reviewed || "October 2026";

  const byCountry = useMemo(() => {
    const map = {};
    for (const c of contacts) {
      (map[c.country] = map[c.country] || []).push(c);
    }
    return map;
  }, [contacts]);

  return (
    <div className="bg-jawa-ivory" data-testid="emergency-page">
      <Nav />
      <Seo
        title="Emergency Contact Numbers — Nigeria & Beyond | JÀWÀ Africa"
        description="Verified emergency numbers for Nigeria, Ghana, Kenya, Rwanda, South Africa and Tanzania — displayed free, no download needed."
      />
      <main className="mx-auto max-w-5xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline="Free resource — always visible" title="Emergency contacts." />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
            Save these before you travel. Numbers are verified against public government listings
            (last reviewed {reviewed}) — coverage and response times vary by state and network, so confirm
            the working local numbers on arrival and always save your accommodation's own contacts.
          </p>
        </FadeUp>

        <div className="mt-12 space-y-8">
          {Object.entries(byCountry).map(([country, list]) => (
            <FadeUp key={country}>
              <section className="border border-jawa-stone bg-white" data-testid={`emergency-${country.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h2 className="border-b border-jawa-stone bg-jawa-espresso px-6 py-4 font-serif text-xl font-medium text-jawa-ivory">
                  {country}
                </h2>
                <ul className="divide-y divide-jawa-stone/70">
                  {list.map((c) => (
                    <li key={`${c.label}-${c.number}`} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
                      <div className="max-w-xl">
                        <p className="text-sm font-medium text-jawa-espresso">{c.label}</p>
                        {c.note && <p className="mt-0.5 text-xs text-jawa-espresso/55">{c.note}</p>}
                      </div>
                      <a
                        href={`tel:${c.number.split("/")[0].trim()}`}
                        data-testid={`emergency-call-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="inline-flex items-center gap-2 bg-jawa-coral px-4 py-2 font-mono text-sm font-semibold text-white transition-colors hover:bg-jawa-espresso"
                      >
                        <PhoneCall className="h-4 w-4" /> {c.number}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-10 border border-jawa-gold/40 bg-white p-6" data-testid="emergency-disclaimer">
          <p className="text-xs leading-relaxed text-jawa-espresso/60">
            In any emergency, contact local services first, then your travel insurer and your nearest embassy or
            consulate. JÀWÀ concierge clients can also WhatsApp us on +234 915 437 9303 for guidance — we are not
            an emergency service and never replace one.
          </p>
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
